// Amplitude Experiment client -- the one place that talks to the flag service.
//
// The deployment key is environment-injected (REACT_APP_AMPLITUDE_DEPLOYMENT_KEY),
// never hardcoded: development builds get the dev deployment (100% rollout) and
// QA/production get the prod deployment (rollout controlled in Amplitude), the
// same way the Cognito ids are supplied. With no key configured every flag
// falls back to DEFAULT_FLAG_VALUE, so a fork or a local checkout without the
// secret still builds and runs -- just with the flagged features off.
//
// Evaluation is client-side only. No backend change was needed for this: the
// flag decides what the UI renders, not what the API returns.

import {Experiment} from "@amplitude/experiment-js-client";

import {DEFAULT_FLAG_VALUE} from "./flags";

const DEPLOYMENT_KEY = process.env.REACT_APP_AMPLITUDE_DEPLOYMENT_KEY;

/** Whether a deployment key was injected at build time. */
export const isExperimentConfigured = () => Boolean(DEPLOYMENT_KEY);

// Local overrides: a JSON map of flag key -> variant value, e.g.
//   REACT_APP_FEATURE_FLAG_OVERRIDES={"video-platform-videos-language":"show"}
//
// This exists so a developer can see BOTH sides of a flag without an Amplitude
// account, a working deployment key, or a network round trip -- the "show" path
// is otherwise untestable on a laptop.
//
// Deliberately dead in production builds. NODE_ENV is baked in by the CRA
// build, so this whole branch is constant-folded out of the deployed bundle and
// no environment variable can turn a dark feature on in QA or production. The
// flag service stays the only authority there.
const OVERRIDES = (() => {
  if (process.env.NODE_ENV === "production") return {};
  const raw = process.env.REACT_APP_FEATURE_FLAG_OVERRIDES;
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    // A typo in .env must not take the app down -- ignore it and evaluate
    // normally, which means the feature stays off.
    return {};
  }
})();

// One client for the whole app (Experiment.initialize is itself a singleton
// factory keyed by instance name, but we hold the reference so `variant()` can
// be read synchronously from anywhere after the first load).
let client = null;
// The in-flight/settled start() promise, so concurrent hooks share one request.
let startPromise = null;
// The user id the variants in memory belong to, and the load that is putting
// them there. A login or a logout changes the identity, and the variants have
// to be re-fetched for it -- targeting rules are evaluated per user.
//
// The promise is kept alongside the id, not just the id, so a second caller
// asking for the SAME identity waits for the answer instead of returning
// before it has arrived.
let loadedUserId;
let loadPromise = null;
// Monotonic, so a load that fails can tell whether it is still the current one.
// An A -> B -> A transition puts the SAME id on two different loads, so
// comparing ids cannot decide whose cleanup is still relevant.
let loadToken = 0;

const getClient = () => {
  if (!DEPLOYMENT_KEY) return null;
  if (!client) {
    client = Experiment.initialize(DEPLOYMENT_KEY, {
      // The flag service is not on the critical path: if it is slow or down,
      // give up quickly and let the fallback (feature off) stand rather than
      // holding the UI in a "still deciding" state.
      fetchTimeoutMillis: 3000,

      // Both of these override an SDK default, and both matter.
      //
      // throwOnError defaults to FALSE, which makes start() and fetch()
      // resolve even when the request failed. Every failure would then look
      // like a success to loadFlags: the identity stays recorded, the stale
      // variants stay readable, and nothing ever retries. The failure handling
      // below only means anything if failures actually surface.
      throwOnError: true,

      // retryFetchOnFailure defaults to TRUE: the SDK keeps retrying in the
      // background and quietly swaps the variants in when one lands. Nothing
      // reads that. The hooks in HomePage and VideoDashboard have already
      // rendered their answer and have no signal to re-read, so the retry only
      // desynchronises what the client holds from what the UI is showing --
      // and it would go on doing so for the life of the page.
      //
      // Retrying on the next call instead (see the failure path in loadFlags)
      // keeps the two in step: every value the UI shows came from a request
      // the UI actually waited for.
      retryFetchOnFailure: false,

      fallbackVariant: {value: DEFAULT_FLAG_VALUE},
    });
  }
  return client;
};

/**
 * Load the variants for `userId` (the learner's email or phone -- the id the
 * flag's targeting rules are written against). Anonymous visitors are fetched
 * without a user id, which still resolves the deployment-wide rollout.
 *
 * Never rejects: a failed fetch leaves the fallback variants in place, which is
 * the correct outcome for a feature that must stay hidden unless explicitly
 * enabled.
 */
export const loadFlags = async (userId) => {
  const experiment = getClient();
  if (!experiment) return null;

  const id = userId || undefined;

  // Already loaded, or loading, for this identity: share that work. Awaiting
  // the existing promise rather than returning straight away is the point --
  // a caller that returns early reports "ready" over variants that have not
  // landed yet, and nothing re-renders to correct it.
  if (loadPromise && loadedUserId === id) {
    await loadPromise;
    return experiment;
  }

  const user = id ? {user_id: id} : {};
  const previous = loadPromise;
  const token = ++loadToken;
  loadedUserId = id;

  // Chained onto the previous load rather than started alongside it.
  //
  // fetch() writes the variants held by the ONE shared client, so two loads in
  // flight together race to be the last writer. On an A -> B -> A transition
  // both B's fetch and the second A's fetch would hang off the same start(),
  // run concurrently, and if B landed last the client would hold B's variants
  // while loadedUserId said A -- the hook then reads the wrong user's toggle
  // state, and nothing re-renders to correct it.
  //
  // Serializing makes the last load queued the last one to write, and that is
  // the load whose identity loadedUserId holds. Ordering, not a token check:
  // fetch() has already mutated the client by the time a token could be
  // compared, so there is no stale result left to discard.
  loadPromise = Promise.resolve(previous)
    // A predecessor's failure is its own to report, and must not stop this
    // load from running.
    .catch(() => {})
    .then(() => {
      // start() pulls the flag configs and fetches this user's variants in one
      // call; once it has run, an identity change is a plain fetch().
      if (!startPromise) {
        startPromise = experiment.start(user);
        return startPromise;
      }
      // The variants the client is holding belong to the PREVIOUS identity.
      // Drop them before asking for this one's, so a request that never lands
      // leaves nothing to read: the honest answer for a learner we failed to
      // evaluate is "unknown" -- which is off -- and not the answer that
      // belonged to somebody else. Without this, an anonymous visitor
      // evaluated as `show` would hand that `show` to the next learner to sign
      // in if their own fetch timed out, showing them a flag that may well be
      // switched off for them.
      experiment.clear();
      return experiment.fetch(user);
    })
    .catch(() => {
      // Offline, bad key, or timeout. Forget the identity so the NEXT caller
      // retries, instead of reading it as already loaded and trusting variants
      // that never arrived. Without this a single failed request at login pins
      // the whole session to the wrong evaluation.
      //
      // Only when this is still the current load -- a newer one has already
      // taken the state over, and must not have it cleared underneath it.
      if (loadToken === token) {
        loadedUserId = undefined;
        loadPromise = null;
        startPromise = null;
      }
    });

  await loadPromise;
  return experiment;
};

/**
 * The current value of a flag, read from whatever loadFlags() last resolved.
 * Synchronous and safe to call before the first load: it returns `fallback`
 * until the variants arrive.
 *
 * A local override wins over the service, and over the "no key configured"
 * path, so the flag can be driven from .env on a laptop. Development only --
 * see OVERRIDES.
 */
export const flagValue = (flagKey, fallback = DEFAULT_FLAG_VALUE) => {
  // hasOwnProperty, not `in`: `in` walks the prototype chain, so a flag keyed
  // "constructor" or "toString" would resolve to an Object.prototype member.
  if (Object.prototype.hasOwnProperty.call(OVERRIDES, flagKey)) {
    return OVERRIDES[flagKey];
  }
  const experiment = getClient();
  if (!experiment) return fallback;
  try {
    return experiment.variant(flagKey, {value: fallback})?.value ?? fallback;
  } catch {
    return fallback;
  }
};
